<template>
  <div class="flex flex-col h-full overflow-hidden">
    <report-progress :showProgress="isLoading" />

    <!-- Stats summary cards -->
    <div v-if="stats" class="flex flex-wrap gap-3 px-1 pb-3 flex-shrink-0">
      <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {{ $t("reports.ai_chat_transcript.stat_total_chats") }}
        </div>
        <div class="text-2xl font-bold text-primary-900 mt-0.5">
          {{ stats.total_chats ?? "-" }}
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {{ $t("reports.ai_chat_transcript.stat_avg_duration") }}
        </div>
        <div class="text-2xl font-bold text-primary-900 mt-0.5">
          {{ formatDuration(stats.avg_duration) }}
        </div>
      </div>
      <div class="bg-white rounded-xl border border-gray-200 px-4 py-3 min-w-[150px]">
        <div class="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {{ $t("reports.ai_chat_transcript.stat_escalation_rate") }}
        </div>
        <div class="text-2xl font-bold text-primary-900 mt-0.5">
          {{ stats.escalation_rate != null ? stats.escalation_rate + "%" : "-" }}
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-hidden">
      <div class="h-full flex flex-col">
        <div class="flex-1 overflow-auto">
          <table class="w-full table-auto text-sm">
            <thead class="sticky top-0 z-10">
              <tr>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-s-lg mx-1 px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_conversation_id") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_customer") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_channel") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_outcome") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_started") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium px-2 py-2">
                  {{ $t("reports.ai_chat_transcript.head_ended") }}
                </th>
                <th class="!bg-primary-900 text-white set-border text-center font-weight-medium rounded-e-lg px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(chat, index) in reportData"
                :key="chat.conversation_id || index"
                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
              >
                <td class="text-center !py-2 px-2 border-b border-gray-200 break-all font-mono text-xs">
                  {{ chat.conversation_id }}
                </td>
                <td class="text-center text-indigo px-2 border-b border-gray-200" dir="ltr">
                  {{ chat.customer || "-" }}
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  <v-tooltip :text="channelLabel(chat.channel)" location="top">
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="inline-flex items-center gap-1 justify-center">
                        <v-icon size="small">{{ channelIcon(chat.channel) }}</v-icon>
                        <span class="text-xs text-gray-600">{{ channelLabel(chat.channel) }}</span>
                      </span>
                    </template>
                  </v-tooltip>
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  <span
                    v-if="chat.outcome"
                    class="inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide"
                    :class="outcomeChipClass(chat.outcome)"
                  >
                    {{ outcomeLabel(chat.outcome) }}
                  </span>
                  <span v-else>-</span>
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{ chat.started_at || "-" }}
                </td>
                <td class="text-center px-2 border-b border-gray-200">
                  {{ chat.ended_at || "-" }}
                </td>
                <td class="px-2 border-b border-gray-200">
                  <div class="flex justify-center items-center gap-1">
                    <div
                      class="bg-primary-900 rounded hover:bg-primary-500 p-1 text-white cursor-pointer"
                      :title="$t('reports.ai_chat_transcript.view_transcript')"
                      @click="openTranscript(chat)"
                    >
                      <v-icon size="small">mdi-message-text-outline</v-icon>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ReportProgress from "../../components/ReportProgress.vue";

export default {
  name: "ai-chat-transcript-report",
  components: { ReportProgress },
  props: {
    selectedFilters: { type: Object, default: () => ({}) },
    reportData: { type: Array, default: () => [] },
    totalCount: { type: Number, default: 0 },
    showCount: { type: Boolean, default: false },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      stats: null,
    };
  },
  methods: {
    ...mapActions(["GetAIChatStats"]),

    channelIcon(type) {
      switch (Number(type)) {
        case 1: return "mdi-message";
        case 2: return "mdi-email";
        case 3: return "mdi-whatsapp";
        case 4: return "mdi-send-circle";
        case 5: return "mdi-facebook";
        case 6: return "mdi-instagram";
        case 7: return "mdi-twitter";
        case 8: return "mdi-web";
        default: return "mdi-message-text";
      }
    },
    channelLabel(type) {
      const key = "reports.ai_chat_transcript.channels." + Number(type);
      if (this.$te(key)) return this.$t(key);
      return this.$t("reports.ai_chat_transcript.channels.default") + " " + (type ?? "");
    },
    outcomeLabel(outcome) {
      const key = "reports.ai_chat_transcript.outcomes." + String(outcome).toLowerCase();
      return this.$te(key) ? this.$t(key) : outcome;
    },
    outcomeChipClass(outcome) {
      const o = String(outcome).toLowerCase();
      if (o === "escalated") return "bg-orange-100 text-orange-800";
      if (o === "abandoned") return "bg-gray-200 text-gray-700";
      if (o === "resolved" || o === "completed" || o === "closed") return "bg-green-100 text-green-800";
      return "bg-blue-100 text-blue-800";
    },
    formatDuration(seconds) {
      const total = Math.floor(Number(seconds));
      if (!Number.isFinite(total) || total < 0) return "-";
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      const pad = (n) => String(n).padStart(2, "0");
      return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },

    openTranscript(chat) {
      this.$router.push({
        name: "chat-transcript-details",
        query: { conversation_id: chat.conversation_id },
      });
    },

    async fetchStats() {
      if (!this.selectedFilters?.from || !this.selectedFilters?.to) return;
      try {
        const resp = await this.GetAIChatStats({
          from: this.selectedFilters.from,
          to: this.selectedFilters.to,
        });
        this.stats = resp && resp.success ? resp.stats : null;
      } catch (e) {
        this.stats = null;
      }
    },
  },
  mounted() {
    this.fetchStats();
  },
};
</script>

<style scoped></style>
